import React from 'react';

import { css, cx } from '@emotion/css';
import { Typography } from '@mui/material';
import MuiCardHeader from '@mui/material/CardHeader';

import { FontSizes } from '@/assets/fonts.ts';
import { Icons } from '@/assets/icons.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { Button } from '@/uiKit/atoms/Button.tsx';
import { AtomProps, StyleClasses } from '@/uiKit/atoms/utils/AtomTypes.ts';

export type BaseCardHeaderProps = {
  title: string;
  variant: 'plain' | 'contained';
  onExit?: () => void;
};

export type CardHeaderProps = AtomProps<
  BaseCardHeaderProps,
  StyleClasses<'root' | 'title' | 'exitButton' | 'exitIcon'>
>;

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  variant,
  onExit,
  className,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  return (
    <MuiCardHeader
      className={cx(
        styles.root,
        {
          [styles.plain]: variant === 'plain',
          'MuiCardHeader-plain': variant === 'plain',
          [styles.contained]: variant === 'contained',
          'MuiCardHeader-contained': variant === 'contained',
        },
        className,
        classes?.root,
      )}
      title={
        <Typography variant="h5" className={cx(styles.title, classes?.title)}>
          {title}
        </Typography>
      }
      action={
        onExit && (
          <Button
            className={cx(styles.exitButton, classes?.exitButton)}
            variant="contained"
            startIcon={
              <Icons.exit className={cx(styles.exitIcon, classes?.exitIcon)} />
            }
            onClick={onExit}
          />
        )
      }
      {...props}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    width: '100%',
    padding: theme.spacing(10, 12),
    gap: theme.spacing(16),
    '& .MuiCardHeader-action': {
      position: 'absolute',
      top: 17,
      right: theme.spacing(12),
    },
  }),
  plain: css({}),
  contained: css({
    backgroundColor: theme.palette.background.default,
  }),
  title: css({
    width: '100%',
    textAlign: 'center',
    color: theme.palette.primary.main,
  }),
  exitButton: css({
    borderRadius: '100%',
    padding: theme.spacing(1),
  }),
  exitIcon: css({
    width: FontSizes.h4,
    height: FontSizes.h4,
  }),
});
